import React, { useEffect, useState } from 'react';
import { List, arrayMove } from 'react-movable';

import { makeStyles } from '@material-ui/styles';

import { CareTeamMember } from '@vestahealthcare/common/models';

import { BackgroundColors, Colors } from 'styleguide-v2';

import { ContactInfoCTMRow } from './ContactInfoCTMRow';
import { ContactInfoMemberRow } from './ContactInfoMemberRow';
import { ContactInfoSortableListItem } from './types';

type Props = {
  className?: string;
  'data-cy'?: string;
  getLabel?: (item: ContactInfoSortableListItem) => string;
  items: ContactInfoSortableListItem[];
  onChange: (items: ContactInfoSortableListItem[]) => void;
};

const useStyles = makeStyles({
  row: {
    background: BackgroundColors.lighterGray,
    margin: '5px 0',
    padding: '5px',
    borderRadius: '5px',
    boxShadow: 'rgba(0, 0, 0, 0.15) 1px 2px 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    color: Colors.lightGray,
    width: '20px',
    margin: '5px 5px',
    padding: '5px',
  },
  itemContainer: {
    alignItems: 'center',
    border: 0,
    background: 'transparent',
    display: 'inline-flex',
    flex: 'auto',
    height: '34px',
  },
  itemWrapper: {
    width: '100%',
    display: 'flex',
  },
});

export const ContactInfoSortableList = (props: Props) => {
  const styles = useStyles(props);
  const { className, 'data-cy': dataCy, getLabel, items, onChange } = props;
  const [list, setList] = useState<ContactInfoSortableListItem[]>(items || []);

  useEffect(() => {
    setList([...items]);
  }, [items]);

  const renderItem = (item: ContactInfoSortableListItem, index: number) => {
    if (item instanceof CareTeamMember)
      return (
        <ContactInfoCTMRow
          ctm={item}
          editMode
          index={index}
          key={`contact-caregiver-${item.id}`}
        />
      );
    return (
      <ContactInfoMemberRow
        editMode
        member={item}
        index={index}
        key={`contact-member-${item.id}`}
      />
    );
  };

  return (
    <List
      lockVertically
      values={list}
      onChange={({ oldIndex, newIndex }) => {
        setList([...arrayMove(list, oldIndex, newIndex)]);
        onChange([...arrayMove(list, oldIndex, newIndex)]);
      }}
      renderList={({ children, props }) => (
        <div data-cy={dataCy} className={className} {...props}>
          {children}
        </div>
      )}
      renderItem={({ value: item, props, isDragged, index = 0 }) => {
        const row = (
          <div {...props} className={styles.row} style={{ zIndex: 1 }}>
            <div className="flex">
              <i
                data-movable-handle
                className={`${styles.icon} fa fa-bars`}
                style={{
                  cursor: isDragged ? 'grabbing' : 'grab',
                }}
              />
              <div className={styles.itemWrapper} tabIndex={index}>
                <div className={styles.itemContainer}>
                  {getLabel ? getLabel(item) : renderItem(item, index)}
                </div>
              </div>
            </div>
          </div>
        );
        return isDragged ? (
          <div
            // eslint-disable-next-line react/prop-types
            style={{ ...props.style, borderSpacing: 5, zIndex: 10000 }}
          >
            {row}
          </div>
        ) : (
          row
        );
      }}
    />
  );
};

export default ContactInfoSortableList;
